import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);

// Simple uuid for cart line ids (each product+addons combo is a distinct line)
const lineId = () => `l_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;

const hamperLinePrice = (item) =>
  item.price + (item.addons || []).reduce((s, a) => s + a.price * a.quantity, 0);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("bs_cart");
      const parsed = raw ? JSON.parse(raw) : [];
      // migrate old-format items (no line_id, no addons)
      return parsed.map((it) => ({
        line_id: it.line_id || lineId(),
        addons: it.addons || [],
        ...it,
      }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("bs_cart", JSON.stringify(items));
  }, [items]);

  // Add a hamper (with optional pre-selected addons) as a NEW cart line.
  // Different addon selections for the same product are distinct lines so
  // shoppers can gift multiple hampers with different fillings.
  const addItem = (product, quantity = 1, addons = [], selectedToyIds = []) => {
    if ((product.product_type || "hamper") === "chocolate") {
      toast.info("Customised chocolates are enquiry-only — please send an enquiry.");
      return;
    }
    const productToys = product.toys || [];
    const chosenToys = selectedToyIds
      .map((tid) => productToys.find((t) => t.id === tid))
      .filter(Boolean)
      .map((t) => ({ toy_id: t.id, name: t.name, image_url: t.image_url || t.image_base64 || "" }));

    const newLine = {
      line_id: lineId(),
      product_id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || product.image_base64 || "",
      product_type: product.product_type || "hamper",
      quantity,
      addons: addons.map((a) => ({
        addon_id: a.id,
        name: a.name,
        price: a.price,
        image_url: a.image_url || a.image_base64 || "",
        quantity: a.quantity,
      })),
      selected_toy_ids: selectedToyIds,
      selected_toys: chosenToys,
    };
    setItems((prev) => [...prev, newLine]);
    toast.success(`${product.name} added to cart`);
  };

  const updateQty = (line_id, quantity) => {
    if (quantity <= 0) return removeItem(line_id);
    setItems((prev) => prev.map((it) => (it.line_id === line_id ? { ...it, quantity } : it)));
  };

  const removeItem = (line_id) => {
    setItems((prev) => prev.filter((it) => it.line_id !== line_id));
  };

  const clearCart = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + hamperLinePrice(it) * it.quantity, 0),
    [items],
  );
  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);
  const hasHamper = useMemo(
    () => items.some((it) => (it.product_type || "hamper") === "hamper"),
    [items],
  );

  return (
    <CartCtx.Provider
      value={{ items, addItem, updateQty, removeItem, clearCart, subtotal, count, hamperLinePrice, hasHamper }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
